import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { db } from "../firebase";
import { collection, query, where, getDocs, writeBatch, doc, increment, serverTimestamp } from "firebase/firestore";

function CancelInvoices() {
  const navigate = useNavigate();
  const [parties, setParties] = useState([]);
  const [selectedParty, setSelectedParty] = useState("");
  const [dateFilter, setDateFilter] = useState(new Date().toISOString().slice(0, 10));
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchParties = async () => {
      const snap = await getDocs(collection(db, "partyMaster"));
      setParties(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };
    fetchParties();
  }, []);

  const searchInvoices = async () => {
    if (!selectedParty || !dateFilter) {
      alert("Please select both a Date and a Party to search.");
      return;
    }
    setLoading(true);
    try {
      const q = query(
        collection(db, "orders"),
        where("partyId", "==", selectedParty),
        where("approvedDateOnly", "==", dateFilter),
        where("status", "==", "APPROVED")
      );
      const snap = await getDocs(q);
      setInvoices(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error(err);
      alert("Failed to fetch invoices");
    }
    setLoading(false);
  };

  const handleCancel = async (invoice) => {
    // SECURITY: Ledger Boundary
    if (invoice.ledgerSynced === true) {
      alert("🚫 SETTLEMENT BLOCK: This invoice is permanently sealed inside the Cashier's synced ledger. Canceling it now would violently shatter historical financial integrity.");
      return;
    }

    // Dynamic password validation (Strictly IST)
    const istNow = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
    const ddmmyy = String(istNow.getDate()).padStart(2, '0') + 
                   String(istNow.getMonth() + 1).padStart(2, '0') + 
                   String(istNow.getFullYear()).slice(-2);
                   
    const pwd = prompt("Enter the dynamic PIN (ddmmyy) to forcefully cancel this invoice:");
    if (pwd !== ddmmyy) {
      alert("🚫 Incorrect PIN. Cancellation Aborted.");
      return;
    }

    if (!window.confirm(`Are you extremely certain you want to cancel Invoice: ${invoice.displayOrderId}? This action is irreversible.`)) {
      return;
    }

    try {
      setLoading(true);
      const batch = writeBatch(db);
      
      // 1. Root Order
      const orderRef = doc(db, "orders", invoice.id);
      batch.update(orderRef, { status: "CANCELLED" });

      // 2. Order Lines + 4. Reverse Stock Atomically
      const linesSnap = await getDocs(query(collection(db, "orderLines"), where("orderId", "==", invoice.id)));
      linesSnap.docs.forEach(d => {
        const lineId = d.id;
        const lineData = d.data();

        // Kill the line item
        batch.update(doc(db, "orderLines", lineId), { status: "CANCELLED" });

        // Safely extract true db-level SKU and quantity
        const sku = lineData.sku;
        const qty = Number(lineData.qty || 0);

        if (sku && qty > 0) {
            const stockDocId = `${invoice.approvedDateOnly}_${sku}`;
            const stockRef = doc(db, "daily_stock", stockDocId);
            batch.set(stockRef, {
                date: invoice.approvedDateOnly,
                sku: sku,
                updatedAt: serverTimestamp(),
                invoiceQty: increment(-qty) // Zero-read robust reversal injection
            }, { merge: true });
        }
      });

      // 3. Outlet Orders + ANNUAL LIMIT REVERSAL
      const outletSnap = await getDocs(query(collection(db, "outletOrders"), where("rootOrderId", "==", invoice.id)));
      
      const outletReversalMap = {}; // Aggregate amount per outlet
      outletSnap.docs.forEach(d => {
        const oData = d.data();
        const oId = oData.outletId;
        const amt = Number(oData.totalAmount || 0);

        batch.update(doc(db, "outletOrders", d.id), { status: "CANCELLED" });

        if (oId && amt > 0) {
          outletReversalMap[oId] = (outletReversalMap[oId] || 0) + amt;
        }
      });

      // 4. Atomic Increment (Negative) to refund the annual limit
      for (const [oId, totalAmt] of Object.entries(outletReversalMap)) {
        batch.update(doc(db, "outletMaster", oId), {
          annualUsed: increment(-totalAmt)
        });
      }

      await batch.commit();

      alert(`✅ Invoice ${invoice.displayOrderId} successfully cancelled and stock reversed!`);
      // Re-fetch
      searchInvoices();
    } catch (err) {
      console.error(err);
      alert("Failed to cancel invoice. See console.");
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div style={{ padding: 20 }}>
        <h2 style={{ color: "#d32f2f", borderBottom: "2px solid #ffcdd2", paddingBottom: 10 }}>🚨 Force Cancel Invoices</h2>
        <p style={{ color: "#666", marginBottom: 20 }}>
          This utility is heavily restricted. Cancelling an invoice forcefully reverts the status across all tables (Orders, OrderLines, OutletOrders) and performs a live inventory reversal injection.
        </p>

        <div style={{ display: "flex", gap: "15px", marginBottom: "30px", background: "#f8f9fa", padding: "20px", borderRadius: "8px" }}>
          <input 
            type="date" 
            value={dateFilter} 
            onChange={e => setDateFilter(e.target.value)} 
            style={{ padding: "10px", borderRadius: "5px", border: "1px solid #ccc", minWidth: "200px" }}
          />

          <select 
            value={selectedParty} 
            onChange={e => setSelectedParty(e.target.value)}
            style={{ padding: "10px", borderRadius: "5px", border: "1px solid #ccc", minWidth: "300px" }}
          >
            <option value="">-- Choose Party --</option>
            {parties.map(p => (
              <option key={p.id} value={p.partyId}>
                {p.partyId === p.name ? p.name : `${p.partyId} - ${p.name}`}
              </option>
            ))}
          </select>

          <button 
            onClick={searchInvoices} 
            disabled={loading}
            style={{ background: "#1976d2", color: "#fff", border: "none", padding: "10px 20px", borderRadius: "5px", cursor: "pointer" }}
          >
            {loading ? "Searching..." : "🔍 Find Valid Invoices"}
          </button>
        </div>

        {invoices.length > 0 ? (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#eee", textAlign: "left" }}>
                <th style={{ padding: 12, border: "1px solid #ddd" }}>Display ID</th>
                <th style={{ padding: 12, border: "1px solid #ddd" }}>Party</th>
                <th style={{ padding: 12, border: "1px solid #ddd" }}>Items/SKUs</th>
                <th style={{ padding: 12, border: "1px solid #ddd" }}>Total Amount</th>
                <th style={{ padding: 12, border: "1px solid #ddd" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.id}>
                  <td style={{ padding: 12, border: "1px solid #ddd", fontWeight: "bold" }}>{inv.displayOrderId}</td>
                  <td style={{ padding: 12, border: "1px solid #ddd" }}>{inv.partyId}</td>
                  <td style={{ padding: 12, border: "1px solid #ddd" }}>{Object.keys(inv.items || {}).length} SKUs</td>
                  <td style={{ padding: 12, border: "1px solid #ddd", color: "#1976d2", fontWeight: "bold" }}>₹ {Number(inv.totalAmount || 0).toFixed(2)}</td>
                  <td style={{ padding: 12, border: "1px solid #ddd", textAlign: "center" }}>
                    <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                      <button 
                        onClick={() => {
                          if (inv.ledgerSynced === true) {
                            alert("🚫 SETTLEMENT BLOCK: This invoice is locked in the synced ledger and cannot be edited.");
                            return;
                          }
                          navigate(`/edit-approved-order/${inv.id}`);
                        }}
                        style={{ background: "#ff9800", color: "#fff", border: "none", padding: "8px 16px", borderRadius: "4px", cursor: "pointer", fontWeight: "bold" }}
                      >
                        ✏️ EDIT
                      </button>
                      <button 
                        onClick={() => handleCancel(inv)}
                        style={{ background: "#dc3545", color: "#fff", border: "none", padding: "8px 16px", borderRadius: "4px", cursor: "pointer", fontWeight: "bold" }}
                      >
                        ✕ CANCEL
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={{ padding: 40, textAlign: "center", color: "#888", border: "1px dashed #ccc", borderRadius: 8 }}>
            No approved invoices found for this date & party combination.
          </div>
        )}

      </div>
    </Layout>
  );
}

export default CancelInvoices;
